import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getPlan, type PlanId } from '../config/billing';
import { normalizePlanName } from '../lib/subscription-utils';

// ✅ Cache désactivé - variables gardées pour référence future
// Cache global pour éviter les re-vérifications
// const permissionCache = new Map<string, {
//   data: any;
//   timestamp: number;
//   expiresIn: number;
// }>();
// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export interface SubscriptionPlan {
  name: string;
  display_name: string;
  price_monthly: number;
  price_yearly: number;
  max_projects: number;
  max_clients: number;
  max_storage_gb: number;
  max_team_members: number;
  features: string[];
}

export interface UserSubscription {
  plan_name: string;
  plan_display_name: string;
  status: string;
  billing_cycle: string;
  amount: number;
  currency: string;
  end_date: string | null;
  features: string[];
}

export interface SubscriptionLimits {
  maxClients: number;
  maxOrders: number;
  maxProjects: number;
  maxStorage: number;
  maxTeamMembers: number;
}

export interface PagePermissions {
  dashboard: boolean;
  clients: boolean;
  orders: boolean;
  calendar: boolean;
  referrals: boolean;
  workboard: boolean;
  stats: boolean;
  invoices: boolean;
  assistant: boolean; // AI Assistant - Scale only
  admin: boolean;
}

export const useSubscriptionPermissions = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [limits, setLimits] = useState<SubscriptionLimits>({
    maxClients: 5,
    maxOrders: 10,
    maxProjects: 1,
    maxStorage: 1,
    maxTeamMembers: 1
  });
  const [permissions, setPermissions] = useState<PagePermissions>({
    dashboard: true,
    clients: true,
    orders: true,
    calendar: false,
    referrals: false,
    workboard: false,
    stats: false,
    invoices: false,
    assistant: false,
    admin: false
  });
  const [loading, setLoading] = useState(true);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (user && !isInitializedRef.current) {
      loadSubscriptionData();
    } else if (!user) {
      setLoading(false);
      isInitializedRef.current = false;
    }
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user || isInitializedRef.current) return;
    if (!supabase) {
      console.error('❌ Supabase client is not available');
      setLoading(false);
      return;
    }

    // ✅ Cache désactivé - code nettoyé pour éviter la confusion
    // const cacheKey = `permissions_${user.id}`;
    // const now = Date.now();
    // Le cache peut contenir de mauvaises données si la BD a été modifiée manuellement
    // Si besoin de réactiver le cache, décommenter le code ci-dessous et tester soigneusement
    // const cached = permissionCache.get(cacheKey);
    // if (cached && (now - cached.timestamp) < cached.expiresIn) {
    //   console.log('🚀 useSubscriptionPermissions: Utilisation du cache pour', user.id);
    //   setSubscription(cached.data.subscription);
    //   setIsAdmin(cached.data.isAdmin);
    //   setLimits(cached.data.limits);
    //   setPermissions(cached.data.permissions);
    //   setLoading(false);
    //   isInitializedRef.current = true;
    //   return;
    // }

    try {
      console.log('🔄 useSubscriptionPermissions: Chargement des permissions pour', user.id);
      
      // Vérifier le statut admin en premier (is_admin ou role)
      const { data: adminData, error: adminError } = await supabase
        .from('user_profiles')
        .select('is_admin, role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (adminError) {
        console.warn('Erreur lors de la récupération du profil admin:', adminError);
        // Continuer avec les valeurs par défaut si le profil n'existe pas
        setIsAdmin(false);
      } else if (adminData) {
        // Considérer admin si is_admin est true OU role est Admin
        const isUserAdmin = adminData.is_admin === true || adminData.role === 'Admin';
        setIsAdmin(isUserAdmin);
        console.log('🔍 Statut admin vérifié:', {
          is_admin: adminData.is_admin,
          role: adminData.role,
          finalIsAdmin: isUserAdmin
        });
      } else {
        // Profil n'existe pas encore
        setIsAdmin(false);
        console.log('⚠️ Profil utilisateur non trouvé pour', user.id, '- valeurs par défaut appliquées');
      }

      // Récupérer l'abonnement actuel depuis user_profiles et stripe_subscriptions
      // IMPORTANT: user_profiles.subscription_plan is the source of truth for the plan name
      let currentSubscription: UserSubscription | null = null;
      let planName = 'launch'; // Default plan
      let profileData: { subscription_plan: string | null; subscription_expires_at: string | null } | null = null;
      
      try {
        // First, get plan from user_profiles (SOURCE OF TRUTH)
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('subscription_plan, subscription_expires_at')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('❌ Error fetching user_profiles:', profileError);
        } else if (profile) {
          profileData = profile;
          if (profile.subscription_plan) {
            // ✅ Use centralized normalizePlanName function for consistency
            // Database stores: 'Boost', 'Scale', 'Lunch' (capitalized)
            // Normalize to lowercase for frontend use while maintaining consistency
            const normalizedPlan = normalizePlanName(profile.subscription_plan);
            planName = normalizedPlan.toLowerCase(); // Frontend uses lowercase
            console.log('📋 Plan from user_profiles:', {
              raw: profile.subscription_plan,
              normalized: normalizedPlan,
              frontend: planName,
              expiresAt: profile.subscription_expires_at
            });
          } else {
            console.warn('⚠️ user_profiles.subscription_plan is NULL, defaulting to launch');
          }
        } else {
          console.warn('⚠️ user_profiles record not found for user:', user.id);
        }

        // ⚠️ FIX CRITIQUE: user_profiles.subscription_plan is the SOURCE OF TRUTH
        // We ALWAYS create a subscription object from user_profiles, regardless of Stripe status
        // Stripe is only used to get dates (current_period_end), not to determine the plan
        
        // Try to get Stripe subscription data for dates (optional - non-blocking)
        let stripePeriodEnd: Date | null = null;
        let stripeStatus: string | null = null;
        
        try {
          const { data: customerData } = await supabase
            .from('stripe_customers')
            .select('customer_id')
            .eq('user_id', user.id)
            .is('deleted_at', null)
            .maybeSingle();

          if (customerData?.customer_id) {
            const { data: stripeSub, error: stripeSubError } = await supabase
              .from('stripe_subscriptions')
              .select('status, cancel_at_period_end, current_period_end')
              .eq('customer_id', customerData.customer_id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (!stripeSubError && stripeSub) {
              stripeStatus = stripeSub.status;
              if (stripeSub.current_period_end) {
                stripePeriodEnd = new Date(stripeSub.current_period_end);
              }
            }
          }
        } catch (stripeError) {
          // Non-critical error - continue with user_profiles data
          console.warn('⚠️ Error fetching Stripe data (non-critical, continuing with user_profiles):', stripeError);
        }

        // NOW: ALWAYS create subscription object from user_profiles.subscription_plan
        // This is the FIX - we don't depend on Stripe to determine the plan
        const now = new Date();
        
        // Determine end_date: prefer user_profiles.subscription_expires_at, then Stripe current_period_end, then null
        let endDate: string | null = null;
        if (profileData?.subscription_expires_at) {
          const expiresAt = new Date(profileData.subscription_expires_at);
          if (expiresAt > now) {
            endDate = expiresAt.toISOString();
          }
        } else if (stripePeriodEnd && stripePeriodEnd > now) {
          endDate = stripePeriodEnd.toISOString();
        }

        // Determine status: if Stripe says active/trialing, use that; otherwise use 'active'
        let subscriptionStatus = 'active';
        if (stripeStatus && (stripeStatus === 'active' || stripeStatus === 'trialing')) {
          subscriptionStatus = 'active'; // Treat both as active
        } else if (stripeStatus && stripeStatus !== 'active' && stripeStatus !== 'trialing') {
          // Stripe says inactive, but if we have a future expiration date, still consider active
          if (endDate) {
            subscriptionStatus = 'active'; // Still active until expiration
          } else {
            subscriptionStatus = stripeStatus;
          }
        }

        // CREATE SUBSCRIPTION OBJECT - ALWAYS from user_profiles
        // This is the KEY FIX: we ALWAYS create currentSubscription if planName exists
        if (planName) {
          const displayName = planName === 'launch' 
            ? 'Lunch' 
            : planName.charAt(0).toUpperCase() + planName.slice(1);

          currentSubscription = {
            plan_name: planName, // FROM user_profiles - SOURCE OF TRUTH
            plan_display_name: displayName,
            status: subscriptionStatus,
            billing_cycle: 'monthly',
            amount: 0,
            currency: 'USD',
            end_date: endDate,
            features: []
          };
          
          console.log('✅ Created subscription from user_profiles (SOURCE OF TRUTH):', {
            planName,
            displayName,
            status: subscriptionStatus,
            endDate,
            hasStripeData: !!stripePeriodEnd,
            stripeStatus
          });
          
          setSubscription(currentSubscription);
        } else {
          // planName is null or undefined - default to launch
          console.warn('⚠️ planName is null/undefined, defaulting to launch');
          planName = 'launch';
          currentSubscription = {
            plan_name: 'launch',
            plan_display_name: 'Lunch',
            status: 'active',
            billing_cycle: 'monthly',
            amount: 0,
            currency: 'USD',
            end_date: null,
            features: []
          };
          setSubscription(currentSubscription);
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement de l\'abonnement:', error);
        // Even if there's an error, try to create subscription from planName if we have it
        // This ensures the UI always shows the correct plan from user_profiles
        if (!currentSubscription && planName) {
          console.log('🔧 FIX: Creating subscription after error (user_profiles is source of truth):', planName);
          const displayName = planName === 'launch' 
            ? 'Lunch' 
            : planName.charAt(0).toUpperCase() + planName.slice(1);
          currentSubscription = {
            plan_name: planName,
            plan_display_name: displayName,
            status: 'active',
            billing_cycle: 'monthly',
            amount: 0,
            currency: 'USD',
            end_date: profileData?.subscription_expires_at || null,
            features: []
          };
          setSubscription(currentSubscription);
        }
      }

      // Log final state for debugging
      console.log('✅ useSubscriptionPermissions final state:', {
        planName,
        hasCurrentSubscription: !!currentSubscription,
        subscriptionPlanName: currentSubscription?.plan_name,
        subscriptionDisplayName: currentSubscription?.plan_display_name,
        profilePlan: profileData?.subscription_plan,
        profileExpiresAt: profileData?.subscription_expires_at,
        subscriptionStatus: currentSubscription?.status,
        subscriptionEndDate: currentSubscription?.end_date
      });

      // Définir les limites selon le plan (illimitées pour les admins)
      // planName is already set from the subscription check above
      const newLimits = getLimitsForPlan(planName);
      const isUserAdmin = adminData ? (adminData.is_admin === true || adminData.role === 'Admin') : false;
      const finalLimits = isUserAdmin ? {
        maxClients: -1,
        maxOrders: -1,
        maxProjects: -1,
        maxStorage: -1,
        maxTeamMembers: -1
      } : newLimits;
      setLimits(finalLimits);
      
      // Définir les permissions selon le plan et le statut admin
      const newPermissions = getPermissionsForPlan(planName, isUserAdmin);
      setPermissions(newPermissions);

      // ✅ Cache désactivé - ne pas mettre en cache pour éviter les problèmes de synchronisation
      // Si besoin de réactiver le cache, décommenter le code ci-dessous
      // permissionCache.set(cacheKey, {
      //   data: {
      //     subscription: currentSubscription,
      //     isAdmin: isUserAdmin || false,
      //     limits: finalLimits,
      //     permissions: newPermissions
      //   },
      //   timestamp: now,
      //   expiresIn: CACHE_DURATION
      // });
      // console.log('✅ useSubscriptionPermissions: Permissions mises en cache pour', user.id);
    } catch (error) {
      console.error('Erreur lors du chargement des données d\'abonnement:', error);
    } finally {
      setLoading(false);
      isInitializedRef.current = true;
    }
  };

  const getLimitsForPlan = (planName: string): SubscriptionLimits => {
    // Use billing.ts as single source of truth
    const planConfig = getPlan(planName as PlanId);
    if (!planConfig) {
      // Fallback to launch plan if plan not found
      const launchPlan = getPlan('launch');
      return {
        maxClients: launchPlan?.limits.maxClients ?? 5,
        maxOrders: launchPlan?.limits.maxOrders ?? 10,
        maxProjects: launchPlan?.limits.maxProjects ?? 1,
        maxStorage: launchPlan?.limits.maxStorage ?? 1,
        maxTeamMembers: launchPlan?.limits.maxTeamMembers ?? 1
      };
    }
    
    return {
      maxClients: planConfig.limits.maxClients,
      maxOrders: planConfig.limits.maxOrders,
      maxProjects: planConfig.limits.maxProjects,
      maxStorage: planConfig.limits.maxStorage,
      maxTeamMembers: planConfig.limits.maxTeamMembers
    };
  };

  const getPermissionsForPlan = (planName: string, isAdmin: boolean = false): PagePermissions => {
    // Les admins ont accès à tout, peu importe leur abonnement
    if (isAdmin) {
      return {
        dashboard: true,
        clients: true,
        orders: true,
        calendar: true,
        referrals: true,
        workboard: true,
        stats: true,
        invoices: true,
        assistant: true,
        admin: true
      };
    }

    // Use billing.ts as single source of truth
    const planConfig = getPlan(planName as PlanId);
    if (!planConfig) {
      // Fallback to launch plan permissions
      return {
        dashboard: true,
        clients: true,
        orders: true,
        calendar: false,
        referrals: false,
        workboard: false,
        stats: false,
        invoices: false,
        assistant: false,
        admin: false
      };
    }

    const permissions = {
      dashboard: planConfig.permissions.dashboard,
      clients: planConfig.permissions.clients,
      orders: planConfig.permissions.orders,
      calendar: planConfig.permissions.calendar,
      referrals: planConfig.permissions.referrals,
      workboard: planConfig.permissions.workboard,
      stats: planConfig.permissions.stats,
      invoices: planConfig.permissions.invoices,
      assistant: planConfig.permissions.assistant, // AI Assistant - Scale only
      admin: false // Admin is not a plan feature
    };

    // Log pour debug - vérifier que assistant est bien activé pour Scale
    if (planName.toLowerCase().includes('scale')) {
      console.log('🔍 getPermissionsForPlan - Scale plan:', {
        planName,
        planConfigId: planConfig.id,
        assistantPermission: permissions.assistant,
        allPermissions: permissions
      });
    }

    return permissions;
  };

  const hasPermission = (page: keyof PagePermissions): boolean => {
    return permissions[page];
  };

  const canAccessPage = (page: keyof PagePermissions): boolean => {
    return hasPermission(page);
  };

  const isWithinLimit = (resource: keyof SubscriptionLimits, currentCount: number): boolean => {
    const limit = limits[resource];
    return limit === -1 || currentCount < limit;
  };

  const getRemainingLimit = (resource: keyof SubscriptionLimits, currentCount: number): number => {
    const limit = limits[resource];
    if (limit === -1) return -1; // Infini
    return Math.max(0, limit - currentCount);
  };

  const refresh = () => {
    if (user) {
      // ✅ Standardisé: refresh() au lieu de refreshSubscription()
      // ✅ Réinitialiser et recharger (cache désactivé)
      // const cacheKey = `permissions_${user.id}`;
      // permissionCache.delete(cacheKey);
      isInitializedRef.current = false;
      loadSubscriptionData();
    }
  };

  const clearCache = () => {
    // ✅ Cache désactivé - juste réinitialiser le flag
    // permissionCache.clear();
    isInitializedRef.current = false;
  };

  return {
    subscription,
    isAdmin,
    limits,
    permissions,
    loading,
    hasPermission,
    canAccessPage,
    isWithinLimit,
    getRemainingLimit,
    refresh, // ✅ Standardisé: refresh() au lieu de refreshSubscription()
    clearCache
  };
};
